import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Slider } from '../ui/slider';
import { Loader2, AlertCircle, Globe2, FileSearch } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { toast } from '../ui/use-toast';

interface DeepCrawlModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  siteName: string;
  siteUrl: string;
  token: string;
  onCrawlStarted?: () => void;
}

export function DeepCrawlModal({
  open,
  onOpenChange,
  siteId,
  siteName,
  siteUrl,
  token,
  onCrawlStarted
}: DeepCrawlModalProps) {
  const [maxPages, setMaxPages] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStartCrawl = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/sites/${siteId}/deep-crawl`,
        { 
          maxPages,
          prioritizeProducts: true,
          useParallelCrawling: true,
          crawlDepth: 5
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: 'Deep Crawl Started',
        description: `Started crawling up to ${maxPages} pages of ${siteName}. This may take a while.`,
      });
      
      onOpenChange(false);
      onCrawlStarted?.();
    } catch (err: any) {
      console.error('Failed to start deep crawl:', err);
      setError(err.response?.data?.message || 'Failed to start deep crawl. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <FileSearch className="w-5 h-5" />
              Deep Crawl Configuration
            </div>
          </DialogTitle>
          <DialogDescription>
            Configure and start an extensive crawl of {siteName}. This will discover and analyze pages with priority on product/service pages.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label>Target Website</Label>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Globe2 className="w-4 h-4" />
              {siteUrl}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <Label htmlFor="max-pages">Maximum Pages to Crawl</Label>
              <span className="text-sm font-medium">{maxPages}</span>
            </div>
            <Slider
              id="max-pages"
              min={10}
              max={2000}
              step={10}
              value={[maxPages]}
              onValueChange={(value) => setMaxPages(value[0])}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10 pages</span>
              <span>2000 pages</span>
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Estimated time:</strong> {Math.ceil(maxPages * 0.5)} - {Math.ceil(maxPages * 1.5)} minutes
              <br />
              <strong>Priority:</strong> Product and service pages will be crawled first
              <br />
              <strong>Performance:</strong> Uses parallel crawling for faster results
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartCrawl}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting Crawl...
              </>
            ) : (
              <>
                <FileSearch className="mr-2 h-4 w-4" />
                Start Deep Crawl
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}